import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, ArrowUpRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { createAdInquiry } from "@/lib/api";

const PACKAGES = [
  {
    id: "banner",
    name: "Banner",
    price: "$250",
    cadence: "/ month",
    blurb: "Own a slot. Start today.",
    features: [
      "Leaderboard banner (970×250) on your choice of section",
      "Rotates across Home, Rankings, Watch or a category",
      "Click-through to your site or campaign",
      "Live within 24 hours of creative approval",
    ],
  },
  {
    id: "spotlight",
    name: "Spotlight",
    price: "$2,500",
    cadence: "/ campaign",
    blurb: "Get on the radar.",
    features: [
      "1 AI-crafted advertorial",
      "Featured in the Sponsored feed",
      "30-day homepage placement",
      "Standard display ad slots",
    ],
  },
  {
    id: "feature",
    name: "Feature",
    price: "$6,000",
    cadence: "/ campaign",
    blurb: "Own the conversation.",
    featured: true,
    features: [
      "3 AI-crafted advertorials",
      "Homepage hero rotation",
      "Premium in-article + sidebar ads",
      "Newsletter inclusion",
      "Social amplification",
    ],
  },
  {
    id: "takeover",
    name: "Takeover",
    price: "Custom",
    cadence: "",
    blurb: "Become the issue.",
    features: [
      "Unlimited advertorials",
      "Category sponsorship",
      "Dedicated branded section",
      "Artist collaboration access",
      "Full-site ad takeover days",
    ],
  },
];

const BUDGETS = ["$250–$1k", "$1k–$2.5k", "$2.5k–$6k", "$6k–$15k", "$15k+", "Let's talk"];

export default function Advertise() {
  const [form, setForm] = useState({
    name: "", company: "", email: "", budget: "", package: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const scrollToForm = (pkg) => {
    if (pkg) set("package", pkg);
    document.getElementById("inquiry-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async () => {
    if (!form.name.trim() || !form.company.trim() || !form.email.trim()) {
      toast.error("Name, company and email are required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Enter a valid email.");
      return;
    }
    setSending(true);
    try {
      await createAdInquiry(form);
      setSent(true);
      toast.success("Inquiry sent — we'll be in touch.");
    } catch (e) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full mt-2 bg-background border border-white/15 px-4 py-3 text-foreground placeholder:text-muted-foreground/60 focus:border-signal outline-none font-sans";
  const label = "font-mono text-[11px] uppercase tracking-widest text-muted-foreground";

  return (
    <div>
      {/* Hero */}
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Partner with Nero Sun</span>
          <h1 className="font-anton text-6xl md:text-8xl uppercase tracking-tight mt-3 leading-[0.9]" data-testid="advertise-title">
            Advertise<br />With The Culture
          </h1>
          <p className="text-muted-foreground text-lg mt-6 max-w-2xl">
            Reach the audience defining global urban culture — where hip-hop, fashion,
            sport, cars and travel converge. Run AI-crafted advertorials that read like
            real features, plus premium display placements across the magazine.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <button
              data-testid="advertise-hero-cta"
              onClick={() => scrollToForm("")}
              className="inline-flex items-center gap-2 bg-signal text-white font-mono text-xs uppercase tracking-widest px-7 py-4 hover:bg-signal-hover transition-colors"
            >
              Request a Campaign <ArrowUpRight className="w-4 h-4" />
            </button>
            <button
              data-testid="advertise-rate-teaser"
              onClick={() => scrollToForm("Banner")}
              className="inline-flex items-baseline gap-2 border border-white/15 px-5 py-3.5 hover:border-signal transition-colors group"
            >
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Display banners from</span>
              <span className="font-mono-stat text-2xl text-signal leading-none">$250</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground group-hover:text-signal transition-colors">/mo →</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border-x border-b border-white/10">
        {[
          ["6", "Culture Verticals"],
          ["AI", "Advertorial Engine"],
          ["100%", "Editorial Quality"],
          ["Global", "Urban Audience"],
        ].map(([n, l]) => (
          <div key={l} className="bg-background p-6">
            <div className="font-mono-stat text-3xl md:text-4xl text-signal">{n}</div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mt-2">{l}</div>
          </div>
        ))}
      </div>

      {/* Rate card */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
        <h2 className="font-anton text-4xl md:text-5xl uppercase tracking-tight">The Rate Card</h2>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mt-10">
          {PACKAGES.map((p) => (
            <div
              key={p.id}
              data-testid={`package-${p.id}`}
              className={`border bg-card p-7 flex flex-col ${
                p.featured ? "border-signal" : "border-white/10"
              }`}
            >
              {p.featured && (
                <span className="self-start font-mono text-[10px] uppercase tracking-widest bg-signal text-white px-2 py-1 mb-4">
                  Most Popular
                </span>
              )}
              <h3 className="font-anton text-3xl uppercase tracking-tight">{p.name}</h3>
              <p className="text-muted-foreground text-sm mt-1">{p.blurb}</p>
              <div className="flex items-baseline gap-1 mt-5">
                <span className="font-mono-stat text-4xl">{p.price}</span>
                <span className="font-mono text-xs text-muted-foreground">{p.cadence}</span>
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-foreground/85">
                    <Check className="w-4 h-4 text-signal mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`package-cta-${p.id}`}
                onClick={() => scrollToForm(p.name)}
                className={`mt-7 font-mono text-xs uppercase tracking-widest px-5 py-3 transition-colors ${
                  p.featured
                    ? "bg-signal text-white hover:bg-signal-hover"
                    : "bg-white text-black hover:bg-gray-200"
                }`}
              >
                Choose {p.name}
              </button>
            </div>
          ))}
        </div>
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mt-6">
          All packages include AI advertorials written in the Nero Sun voice ·{" "}
          <Link to="/studio/advertorial" className="text-signal hover:underline">Preview the builder →</Link>
        </p>
      </div>

      {/* Inquiry form */}
      <div id="inquiry-form" className="border-t border-white/10">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-16">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Let's Build</span>
          <h2 className="font-anton text-4xl md:text-6xl uppercase tracking-tight mt-3 leading-none">
            Request a Campaign
          </h2>

          {sent ? (
            <div className="border border-signal bg-card p-10 mt-10 text-center" data-testid="inquiry-success">
              <Sparkles className="w-8 h-8 text-signal mx-auto" />
              <h3 className="font-anton text-3xl uppercase tracking-tight mt-4">You're In</h3>
              <p className="text-muted-foreground mt-3">
                Thanks {form.name.split(" ")[0]} — your inquiry for {form.company} is in.
                Our partnerships team will reach out within 2 business days.
              </p>
              <Link to="/studio/advertorial" className="inline-flex items-center gap-2 mt-6 font-mono text-xs uppercase tracking-widest text-signal hover:underline">
                Try the Advertorial Builder <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="border border-white/10 bg-card p-6 md:p-8 mt-10 space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className={label}>Your Name *</label>
                  <input data-testid="inquiry-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jordan Reyes" className={inputCls} />
                </div>
                <div>
                  <label className={label}>Company / Brand *</label>
                  <input data-testid="inquiry-company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Atelier Noir" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={label}>Work Email *</label>
                <input data-testid="inquiry-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@brand.com" className={inputCls} />
              </div>
              <div>
                <label className={label}>Package of Interest</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {PACKAGES.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`inquiry-pkg-${p.id}`}
                      onClick={() => set("package", p.name)}
                      className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                        form.package === p.name ? "bg-signal text-white border-signal" : "border-white/15 text-foreground/70 hover:border-signal"
                      }`}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={label}>Budget</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {BUDGETS.map((b) => (
                    <button
                      key={b}
                      data-testid={`inquiry-budget-${b}`}
                      onClick={() => set("budget", b)}
                      className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                        form.budget === b ? "bg-signal text-white border-signal" : "border-white/15 text-foreground/70 hover:border-signal"
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={label}>Tell us about the campaign</label>
                <textarea
                  data-testid="inquiry-message"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  rows={4}
                  placeholder="Goals, product, timing, anything we should know…"
                  className={inputCls + " resize-none"}
                />
              </div>
              <button
                data-testid="inquiry-submit"
                onClick={submit}
                disabled={sending}
                className="inline-flex items-center gap-2 bg-signal text-white font-mono text-xs uppercase tracking-widest px-7 py-4 hover:bg-signal-hover transition-colors disabled:opacity-50"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
                {sending ? "Sending…" : "Send Inquiry"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
